import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@pracownik/ui";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useDocumentTitle } from "@/lib/brand";

interface Section {
  heading: string;
  content: string;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function LegalLayout({
  title,
  lastUpdated,
  sections,
}: {
  title: string;
  lastUpdated: string;
  sections: Section[];
}) {
  const { t } = useTranslation("common");
  useDocumentTitle(title);
  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <BrandLogo />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <ArrowLeft className="size-4" />
                {t("backHome")}
              </Link>
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto grid max-w-6xl gap-12 px-4 py-12 sm:px-6 lg:grid-cols-[14rem_1fr]">
        <aside className="hidden lg:block">
          <nav aria-label={t("tableOfContents")} className="sticky top-24 space-y-1 text-sm">
            <p className="text-eyebrow mb-3 text-text-tertiary">{t("tableOfContents")}</p>
            {sections.map((s) => (
              <a
                key={s.heading}
                href={`#${slug(s.heading)}`}
                className="block truncate rounded-md px-2 py-1.5 text-text-secondary transition-colors hover:bg-accent hover:text-foreground"
              >
                {s.heading}
              </a>
            ))}
          </nav>
        </aside>
        <article className="prose-legal max-w-[42rem]">
          <h1 className="text-fluid-h2">{title}</h1>
          <p className="mt-2 text-sm text-text-tertiary">{lastUpdated}</p>
          {sections.map((s) => (
            <section key={s.heading}>
              <h2 id={slug(s.heading)} className="scroll-mt-24">
                {s.heading}
              </h2>
              <p>{s.content}</p>
            </section>
          ))}
        </article>
      </main>
    </div>
  );
}
